<script setup lang="ts">
// src/SideWinder.Web/src/components/strategy/StrategyLibraryPanel.vue
//
// Task 8: read-only listing of the strategy catalog (GET
// /api/admin/strategy/library) — every registered IStrategyProvider row,
// its default params, and whether it is admin_only (hidden from the
// self-service assignment surface, if one is ever built).
import { ref, onMounted } from 'vue'
import { api, type StrategyCatalogEntry } from '../../api/client'

const library = ref<StrategyCatalogEntry[]>([])
const loading = ref(true)
const loadError = ref<string | null>(null)

async function loadData() {
  loading.value = true
  loadError.value = null
  try {
    library.value = await api.adminStrategyLibrary()
  } catch (e: any) {
    loadError.value = e?.message ?? 'Failed to load strategy library'
  } finally {
    loading.value = false
  }
}
onMounted(loadData)
</script>

<template>
  <div class="card">
    <div class="card-header">
      Strategy Library
      <span class="header-count">{{ library.length }}</span>
    </div>
    <div class="card-body">
      <div v-if="loading" class="loading-text">Loading strategy library…</div>
      <div v-else-if="loadError" class="msg-error">{{ loadError }}</div>
      <div v-else-if="library.length === 0" class="empty-text">No strategies registered</div>
      <div v-else class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>Strategy ID</th>
              <th>Name</th>
              <th>Description</th>
              <th>Provider</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="s in library" :key="s.strategy_id">
              <td class="mono-cell">{{ s.strategy_id }}</td>
              <td>{{ s.display_name }}</td>
              <td class="desc-cell">{{ s.description }}</td>
              <td class="mono-cell">{{ s.provider_class }}</td>
              <td>
                <span class="badge" :class="s.is_active ? 'badge-green' : 'badge-grey'">
                  {{ s.is_active ? 'active' : 'inactive' }}
                </span>
                <span v-if="s.admin_only" class="badge badge-purple">admin-only</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.card {
  background: #161b22;
  border: 1px solid #21262d;
  border-radius: 8px;
  overflow: hidden;
}
.card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: #1c2128;
  border-bottom: 1px solid #21262d;
  font-size: 0.85rem;
  font-weight: 600;
  color: #e6edf3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.header-count {
  font-size: 0.75rem;
  font-weight: 400;
  color: #6e7681;
  background: #21262d;
  padding: 1px 6px;
  border-radius: 10px;
}
.card-body { padding: 0.75rem 1rem; }

.table-wrap { overflow-x: auto; }
.data-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
.data-table th {
  text-align: left;
  padding: 6px 10px;
  font-size: 0.7rem;
  color: #6e7681;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #21262d;
  white-space: nowrap;
}
.data-table td { padding: 7px 10px; border-bottom: 1px solid #161b22; vertical-align: middle; color: #c9d1d9; }
.data-table tbody tr:hover { background: #1c2128; }
.data-table tbody tr:last-child td { border-bottom: none; }

.mono-cell { font-family: 'JetBrains Mono', monospace; font-size: 0.8rem; }
.desc-cell { color: #8b949e; font-size: 0.8rem; max-width: 320px; }

.badge {
  display: inline-block;
  padding: 2px 7px;
  margin-right: 0.3rem;
  border-radius: 3px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: lowercase;
}
.badge-green { background: rgba(63,185,80,0.15); color: #3fb950; border: 1px solid rgba(63,185,80,0.3); }
.badge-grey { background: rgba(110,118,129,0.15); color: #6e7681; border: 1px solid #30363d; }
.badge-purple { background: rgba(163,113,247,0.15); color: #a371f7; border: 1px solid rgba(163,113,247,0.3); }

.loading-text, .empty-text { padding: 0.75rem 0; color: #6e7681; font-size: 0.9rem; font-style: italic; }
.msg-error {
  margin-top: 0.5rem;
  padding: 0.4rem 0.75rem;
  background: rgba(248,81,73,0.1);
  border: 1px solid #f85149;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #f85149;
}

@media (max-width: 600px) {
  .data-table th, .data-table td { padding: 5px 6px; }
}
</style>
